import { replaceHydratedHtml } from '../shared/replace-hydrated-html';
// The last authored tree lets unchanged rune/icon subtrees and live inputs stay owned by their mounts.
const markup = new WeakMap<HTMLElement, { html?: string; tree: DocumentFragment; parent?: HTMLElement; key?: string }>();
const parse = (html: string): DocumentFragment => {
    const template = document.createElement('template'); template.innerHTML = html; return template.content;
};
// Hydrators insert siblings that are absent from the authored tree. They never participate in matching.
const widgetRoot = (node: Node): boolean => node instanceof Element && node.matches('.ts-wrapper, .ts-dropdown');
const identityAttributes = ['id', 'data-ft-ab-resource', 'data-ft-hand-slot', 'data-ft-ab-bar', 'data-ft-ab-slot',
    'data-ability_id', 'data-ft-ability-id', 'data-ft-natural-weapon-id', 'data-ft-ab-action', 'data-ft-ab-key'];
function childKey(node: Node): string {
    if (!(node instanceof Element)) return node.nodeName;
    const identity = identityAttributes.filter(key => node.hasAttribute(key)).map(key => `${key}=${node.getAttribute(key)}`).join('|');
    return `${node.nodeName}|${identity || node.getAttribute('class')?.split(/\s+/)[0] || ''}`;
}
function matchesChild(live: Node, authored: Node): boolean {
    // ftMountIcons replaces this authored placeholder with one SVG, image or tooltip span.
    // Keep matching that island when its button changes (e.g. a dock caster switch).
    if (authored instanceof Element && authored.matches('i.ft-icon')
        && live instanceof Element && live.matches('svg, img, .ft-icon-map')) return true;
    if (live.nodeName !== authored.nodeName) return false;
    if (!(live instanceof Element) || !(authored instanceof Element)) return true;
    const keys = identityAttributes.filter(key => authored.hasAttribute(key));
    if (keys.length) return keys.every(key => live.getAttribute(key) === authored.getAttribute(key));
    const cls = authored.getAttribute('class')?.split(/\s+/)[0];
    return !cls || live.classList.contains(cls);
}
export function rememberPatchMarkup(host: HTMLElement, parent?: HTMLElement): void {
    const key = host.getAttribute('data-ft-ab-resource') ?? '';
    const raw = parent && markup.get(parent)?.tree.querySelector(`[data-ft-ab-resource="${key}"]`);
    const html = raw ? raw.innerHTML : host.innerHTML;
    markup.set(host, { html, tree: parse(html), parent, key });
}
/** Patch only authored differences in a small, stable host. Hydration-only attributes/children are preserved. */
export function patchMarkup(host: HTMLElement, html: string, inserted?: (root: HTMLElement) => void): void {
    const previous = markup.get(host);
    if (!previous) { replaceHydratedHtml(host, html); rememberPatchMarkup(host); inserted?.(host); return; }
    if (previous.html === html) return;
    const next = parse(html);
    const additions: HTMLElement[] = [];
    const added = (node: Node): Node => { if (inserted && node instanceof HTMLElement) additions.push(node); return node; };
    const dispose = (node: Node): void => {
        if (!(node instanceof Element)) return;
        // Subtree helpers visit descendants; a removed keyed child can itself own a widget.
        if (node instanceof HTMLSelectElement) (node as HTMLSelectElement & { tomselect?: { destroy(): void } }).tomselect?.destroy();
        const page = window as Window & { destroyTomSelectIn?: (root: Element) => void; disposeBootstrapTooltipsWithin?: (root: Element) => void };
        page.destroyTomSelectIn?.(node);
        if (node.matches('[data-bs-toggle="tooltip"]')) page._ftDisposeBootstrapTooltipOnElement?.(node);
        page.disposeBootstrapTooltipsWithin?.(node);
    };
    function patch(parent: Node, before: Node, after: Node): void {
        const old = new Set<Node>(before.childNodes), available = new Set<Node>([...parent.childNodes].filter(node => !widgetRoot(node)));
        let cursor: Node | null = parent.firstChild;
        for (const b of after.childNodes) {
            const key = childKey(b);
            const a = [...old].find(node => childKey(node) === key);
            let live: Node | undefined = [...available].find(node => matchesChild(node, a ?? b));
            // Match authored keys only: generated ids and extra classes belong to the widget.
            while (cursor && widgetRoot(cursor)) cursor = cursor.nextSibling;
            if (!a || !live) {
                live = added(b.cloneNode(true)); parent.insertBefore(live, cursor);
            } else {
                old.delete(a); available.delete(live);
                if (live !== cursor) parent.insertBefore(live, cursor);
                if (!a.isEqualNode(b)) live = patchNode(parent, live, a, b);
            }
            cursor = live.nextSibling;
        }
        for (const a of old) {
            const live = [...available].find(node => matchesChild(node, a));
            if (live) { available.delete(live); dispose(live); parent.removeChild(live); }
        }
    }
    function patchNode(parent: Node, live: Node, a: Node, b: Node): Node {
        const replace = (): Node => { const next = added(b.cloneNode(true)); dispose(live); parent.replaceChild(next, live); return next; };
        if (a.nodeType !== b.nodeType || a.nodeName !== b.nodeName) return replace();
        if (a instanceof Element && b instanceof Element && live instanceof Element) {
            if (widgetRoot(live) || live.matches('select.tomselected')) return live;
            // A changed hydrated island must remount from its authored payload; an unchanged one was skipped above.
            if (a.matches('.ft-rune-mount, .ft-icon')) return replace();
            if (a.matches('[data-bs-toggle="tooltip"]') && !b.matches('[data-bs-toggle="tooltip"]')) {
                window._ftDisposeBootstrapTooltipOnElement?.(live);
            }
            for (const attr of a.attributes) if (!b.hasAttribute(attr.name)) live.removeAttribute(attr.name);
            for (const attr of b.attributes) if (a.getAttribute(attr.name) !== attr.value) {
                live.setAttribute(attr.name, attr.value);
                if (attr.name === 'value' && live instanceof HTMLInputElement) live.value = attr.value;
            }
            patch(live, a, b);
        } else if (a.nodeValue !== b.nodeValue) live.nodeValue = b.nodeValue;
        return live;
    }
    patch(host, previous.tree, next);
    markup.set(host, { ...previous, html, tree: next });
    for (const node of additions) if (host.contains(node)) inserted?.(node);
    if (previous.parent) {
        const parent = markup.get(previous.parent), raw = parent?.tree.querySelector(`[data-ft-ab-resource="${previous.key}"]`);
        if (parent && raw) { raw.replaceChildren(next.cloneNode(true)); delete parent.html; }
    }
}

// Direct page scripts use the owning bundle's authored-tree cache, rather than importing another copy.
Object.assign(window, { ftPatchMarkup: patchMarkup, ftRememberPatchMarkup: rememberPatchMarkup });
